import { useMemo } from 'react'

import { usePostHog } from '@posthog/react'

import type { TourId } from '../store'

/**
 * Where a tour was launched from — sent with the `started` event. The two
 * `demo-` sources separate a visitor who *asked* for the tour from one who was
 * auto-offered it, which is the difference between intent and reach.
 */
export type TourSource =
	| 'case-management'
	| 'case-view'
	| 'demo-sidebar'
	| 'demo-autostart'
	| 'demo-replay'

const EVENTS = {
	started: 'guided_tour_started',
	completed: 'guided_tour_completed',
	skipped: 'guided_tour_skipped',
	stepViewed: 'guided_tour_step_viewed',
} as const

export type GuidedTourTracker = {
	trackStarted: (payload: { tourId: TourId; source: TourSource }) => void
	trackCompleted: (payload: { tourId: TourId }) => void
	/**
	 * The visitor bailed out (Skip or Esc). Distinct from `completed`, which now
	 * means "reached the end" only — before this the host reported both as
	 * completed, which made a tour's completion rate unreadable.
	 */
	trackSkipped: (payload: { tourId: TourId; stepIndex: number }) => void
	/** One per step actually shown, so a tour's drop-off point is visible. */
	trackStepViewed: (payload: {
		tourId: TourId
		stepIndex: number
		stepId?: string
	}) => void
}

/**
 * Typed PostHog wrapper for guided tours. Mirrors `useBetaAnnouncementPostHog`
 * so event names live in one place. Memoized for the lifetime of the PostHog
 * client, so the returned methods are safe to pass to handlers.
 */
export function useGuidedTourPostHog(): GuidedTourTracker {
	const posthog = usePostHog()

	return useMemo<GuidedTourTracker>(
		() => ({
			trackStarted: (payload) => {
				posthog.capture(EVENTS.started, payload)
			},
			trackCompleted: (payload) => {
				posthog.capture(EVENTS.completed, payload)
			},
			trackSkipped: (payload) => {
				posthog.capture(EVENTS.skipped, payload)
			},
			trackStepViewed: (payload) => {
				posthog.capture(EVENTS.stepViewed, payload)
			},
		}),
		[posthog],
	)
}
